import { getLinkedInCaptionText } from "@dialogueconsulting/sked-preview";
import React, { useMemo, useEffect } from "react";
import styled from "styled-components";

import { Alert, TooltipWrapper } from "ui";
import { PostStatus } from "components/Posts/Post/index";
import { PAGES } from "constants/Pages";
import { AlertType } from "ui/alert";
import { useSelector } from "react-redux";
import { getUser } from "selectors/commonSelectors";
import { findEffectivePostStatus, getPostStatusList } from "utils/user";
import { hasApprovals } from "utils/feature-gates";
import { getAccount } from "selectors/skedCoreSelectors";

import { useLabels } from "components/Posts/PostForm/components/labels-hooks.tsx";
import { useContentLabelSection } from "@/entities/posts/components/templates/content-label-section.tsx";
import { getAccountPlatformType } from "../../../../entities/accounts";
import { PostLink, shouldShowPostUrl } from "./post-url-preview";

export { PostLink } from "./post-url-preview";

export function DetailsTemplate(props) {
  const user = useSelector(getUser);
  const account = useSelector((state) =>
    getAccount(
      state,
      props?.post?.accountId || props?.post?.accountIds?.[0] || ""
    )
  );

  const {
    isContentLabelFeatureEnabled,
    contentLabelOptions,
    selectedContentLabel,
    isFetchingAccountGroups,
  } = useLabels({
    postFormValue: {
      accountIds: [account?._id],
      labelIds: props?.post?.labelIds || [],
    },
  });
  const { renderLabels } = useContentLabelSection({
    isContentLabelFeatureEnabled,
    contentLabelOptions,
    selectedContentLabel,
    isSummary: true,
    postFormValue: {
      accounts: [account],
    },
    isFetchingAccountGroups,
  });

  const showURL = useMemo(
    () => shouldShowPostUrl(props.post, account),
    [account, props.post]
  );

  return (
    <div className="flex flex-col">
      <DetailsContent>
        <PostStatus post={props.post} />
        <div className="flex flex-wrap">{props.renderAccount?.()}</div>

        {!isFetchingAccountGroups &&
          isContentLabelFeatureEnabled &&
          renderLabels && <LabelsWrap>{renderLabels()}</LabelsWrap>}

        {props.renderDetailsList?.()}
        {props.renderTags && <Tags>{props.renderTags()}</Tags>}
        {showURL && (
          <PostLink
            url={props.post.redirectUrl}
            errorMessage="Unable to load preview for invalid post URL"
          />
        )}
        {props.page === PAGES.UPCOMING && user && hasApprovals(user) && (
          <AlertForUnpublishablePostStatus user={user} post={props.post} />
        )}
      </DetailsContent>
    </div>
  );
}

export const Caption = ({ post, expanded, label }) => {
  const noCaption = (
    <div className="font-inter text-neutral-400 text-sm">
      {post.type === "story"
        ? "No manual publishing notes, or post is set to auto-publish."
        : "No caption entered."}
    </div>
  );

  const showCaptionOrNotesForManualPublishing = useMemo(() => {
    const isStoryType = post.type === "story";
    const isShowNotesForManualPublishing =
      isStoryType && post.publishStoryManually;

    return !isStoryType || isShowNotesForManualPublishing;
  }, [post.publishStoryManually, post.type]);

  if (!post.caption || post.caption.length === 0) {
    return noCaption;
  }

  return (
    <div className="group mt-3">
      {showCaptionOrNotesForManualPublishing && (
        <PostDetail
          label={
            label
              ? label
              : post.type === "story"
                ? "Notes for manual publishing"
                : "Caption"
          }
        >
          <div
            className={
              expanded
                ? "font-inter text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap"
                : "font-inter text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap line-clamp-3 group-hover:line-clamp-none transition-all duration-300"
            }
          >
            {getLinkedInCaptionText(post.caption || "")}
          </div>
        </PostDetail>
      )}
    </div>
  );
};

export const DetailsContent = styled.div`
  padding-top: 1rem;
  > * {
    margin-bottom: 1rem;
  }
`;

export function PostDetail({ label, children }) {
  return (
    <div data-testid="postDetail" className="break-words">
      <div className="text-sm font-gotham font-bold text-neutral-700 mb-1">
        {label}:
      </div>
      <div>{children}</div>
    </div>
  );
}

export const Tags = styled.div.attrs({
  className: "flex flex-col text-base",
})``;

export function Tag(props) {
  return (
    <div className="flex items-center mb-1">
      <div className="mr-2 font-bold">{props.label}:</div>
      <div>{props.children}</div>
    </div>
  );
}

export function TagTooltip(props) {
  const content = props.tags.map((tag, index) =>
    tag?.value ? <div key={`${tag.value}-${index}`}>{tag.value}</div> : null
  );

  return (
    <TooltipWrapper tooltip={content} placement={"bottom"}>
      <div className="cursor-default">{props.children}</div>
    </TooltipWrapper>
  );
}
export function AltTextTags({ post }) {
  let mediaWithAltText = 0;
  if (post.mediaFiles) {
    let filesWithAltText = post.mediaFiles.filter((file) => file.altText);
    mediaWithAltText = filesWithAltText.length;
  }
  // if mediaFiles is empty then use post.altText
  // if mediaFiles is > 0 then use values in that array
  return (
    <div data-testid={`altTextTag-${post._id}`}>
      {post.altText && mediaWithAltText <= 1 && (
        <Tag label="Alt text">{post.altText}</Tag>
      )}
      {mediaWithAltText > 1 && (
        <Tag label="Alt text">
          <span>{mediaWithAltText} media</span>
        </Tag>
      )}
    </div>
  );
}

const AlertForUnpublishablePostStatus = ({ user, post }) => {
  const userCollabSettings = getPostStatusList(user);
  const currentStatus = findEffectivePostStatus(
    userCollabSettings,
    post?.postStatusKey
  );
  if (!currentStatus) {
    // the currently selected status does not exist
    // fixes a bug that caused it to throw an error
    return (
      <Alert type={AlertType.error}>
        <strong>
          This post is set to a status that does not exist or has been deleted.
        </strong>{" "}
        Please update the status of the post.
      </Alert>
    );
  } else if (currentStatus.isPublishingAllowed === true) {
    // if the current status is allowed to publish, then don't show a message
    return null;
  } else {
    return (
      <Alert type={AlertType.warning} icon={true}>
        This post will not be published as it is set to the status{" "}
        <strong>{currentStatus.label}</strong>. Make sure you change the post
        status before its publishing time.
      </Alert>
    );
  }
};

const LabelsWrap = styled.div`
  padding-top: 12px;
`;
